import { useEffect, useMemo, useRef, useState } from 'react';
import { useOnboardingStore } from '@/stores/onboardingStore';
import { useAutomationActive } from '@/hooks/useAutomationActive';
import { STAGE_META } from '@/constants/automation';
import {
  deriveAutomationProgress,
  formatElapsed,
  stageActivityKey,
  type AutomationProgressView,
} from '@/utils/automationProgress';
import type { AutomationError } from '@/types';

/**
 * How long a running stage may report nothing new before we tell the user it
 * is taking longer than usual. Mirrors a real backend stall seen at
 * `calibrating_tone`, which sat at progress 0 for six minutes without ever
 * emitting an error — the silence is what made it feel frozen.
 */
const STALE_MS = 2 * 60 * 1000;

export interface UseAutomationProgressResult extends AutomationProgressView {
  /** True while the pipeline should be presented to the user as running. */
  isActive: boolean;
  error: AutomationError | null;
  /** Live "2m 14s" label, or null when the run is idle or the start is unknown. */
  elapsedLabel: string | null;
  /** The running stage has reported no new activity for a couple of minutes. */
  isStalled: boolean;
  /**
   * One-line "Step 5 of 5: Generating post 3 of 12..." summary for surfaces
   * too small for the full checklist. Null when there is nothing truthful yet.
   */
  stepSummary: string | null;
}

/**
 * The single read model behind every setup surface — the app-shell banner, the
 * gated-page panels and the sidebar indicator. Onboarding automation runs for
 * minutes, so the user gets the real step, the backend's own sub-step message
 * and how long it has been going, rather than an unlabelled spinner (LIL-440).
 *
 * Socket and polling subscriptions are owned by `AutomationProgressTracker`;
 * this hook only reads what they have already written into the store.
 */
export function useAutomationProgress(): UseAutomationProgressResult {
  const isActive = useAutomationActive();
  const stages = useOnboardingStore(s => s.automation.stages);
  const startedAt = useOnboardingStore(s => s.automation.startedAt);
  const error = useOnboardingStore(s => s.automation.error);

  const view = useMemo(() => deriveAutomationProgress(stages), [stages]);
  const elapsedLabel = useElapsedLabel(startedAt, isActive);
  const isStalled = useIsStalled(stageActivityKey(view));

  const stepSummary = useMemo(() => {
    if (!view.currentStage) return null;
    const meta = STAGE_META[view.currentStage.stage];
    // The backend's live message is always more specific than our static
    // description, so it wins whenever one has arrived for this stage.
    const detail = view.detailMessage ?? meta.description;
    return `Step ${view.currentStepNumber} of ${view.totalSteps}: ${detail}`;
  }, [view]);

  return { ...view, isActive, error, elapsedLabel, isStalled, stepSummary };
}

/** Flips true once the activity fingerprint has held still past the threshold. */
function useIsStalled(activityKey: string | null): boolean {
  const lastActivityRef = useRef<{ key: string; at: number } | null>(null);
  const [isStalled, setIsStalled] = useState(false);

  useEffect(() => {
    setIsStalled(false);
    if (!activityKey) {
      lastActivityRef.current = null;
      return;
    }
    if (lastActivityRef.current?.key !== activityKey) {
      lastActivityRef.current = { key: activityKey, at: Date.now() };
    }
    const elapsed = Date.now() - lastActivityRef.current.at;
    if (elapsed >= STALE_MS) {
      setIsStalled(true);
      return;
    }
    const timer = window.setTimeout(() => setIsStalled(true), STALE_MS - elapsed);
    return () => window.clearTimeout(timer);
  }, [activityKey]);

  return isStalled;
}

/** Ticks once a second while the run is active, and not at all when it isn't. */
function useElapsedLabel(startedAt: string | null, isActive: boolean): string | null {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!isActive || !startedAt) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [isActive, startedAt]);

  if (!isActive || !startedAt) return null;
  const started = new Date(startedAt).getTime();
  if (Number.isNaN(started)) return null;
  return formatElapsed(now - started);
}
